"use client"

import { useState, useEffect, useCallback } from "react"
import {
  Search,
  Loader,
  AlertCircle,
  User,
  Clock,
  Calendar,
  ChevronRight,
  CheckCircle,
  Filter,
  ChevronDown,
  Eye,
  ArrowRight,
  FileText,
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { getAllQuizData, getQuizSubmissions } from "./api"

const QuizInfo = () => {
  const navigate = useNavigate()
  const [allQuizzes, setAllQuizzes] = useState([])
  const [submissions, setSubmissions] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState("all")
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [flashMessage, setFlashMessage] = useState(null)

  const selectedClass = useSelector((state) => state.classSubject.selectedClass)
  const selectedSubject = useSelector((state) => state.classSubject.selectedSubject)

  const fetchQuizzes = useCallback(async () => {
    if (!selectedClass) {
      setError("Please select a class and subject.")
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const studentId = localStorage.getItem("studentId")
      const classRoomId = localStorage.getItem("classRoomId")

      if (!classRoomId) {
        throw new Error("No classRoomId found in localStorage")
      }

      // Fetch submissions and quizzes
      const quizResponse = await getAllQuizData(classRoomId)
      const submissionsResponse = await getQuizSubmissions(studentId, classRoomId)

      let quizzes = []
      try {
        quizzes = JSON.parse(quizResponse)
      } catch (e) {
        console.error("Error parsing quiz response:", e)
        quizzes = []
      }

      const sharedQuizzes = quizzes.filter((quiz) => quiz.shareWithStudents === true)

      let submissionsData = []
      if (submissionsResponse) {
        try {
          const parsedSubmissions = JSON.parse(submissionsResponse)
          submissionsData = Array.isArray(parsedSubmissions) ? parsedSubmissions : []
        } catch (e) {
          console.error("Error parsing submissions response:", e)
        }
      }

      setAllQuizzes(sharedQuizzes)
      setSubmissions(submissionsData)
      setError(null)
    } catch (err) {
      console.error("Error loading quizzes:", err)
      setError("Failed to load quizzes. Please try again later.")
    } finally {
      setIsLoading(false)
    }
  }, [selectedClass, selectedSubject])

  useEffect(() => {
    fetchQuizzes()
  }, [fetchQuizzes])

  useEffect(() => {
    if (flashMessage) {
      const timer = setTimeout(() => {
        setFlashMessage(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [flashMessage])

  const handleQuizClick = (quiz) => {
    const status = getQuizStatus(quiz)

    // Prevent navigation for overdue quizzes
    if (status === "overdue") {
      setFlashMessage({
        type: "error",
        message: "This quiz is overdue and can no longer be taken.",
      })
      return
    }

    const submission = submissions.find((sub) => sub.quizId === quiz._id)

    if (submission) {
      navigate(`/quiz/results/${submission._id}`)
    } else {
      navigate(`/quiz/take/${quiz._id}`)
    }
  }

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" }
    return new Date(dateString).toLocaleDateString(undefined, options)
  }

  const isPastDue = (dueDate) => {
    if (!dueDate) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const quizDue = new Date(dueDate)
    quizDue.setHours(0, 0, 0, 0)
    return quizDue < today
  }

  const getQuizStatus = (quiz) => {
    const submission = submissions.find((sub) => sub.quizId === quiz._id)

    if (submission) {
      return "submitted"
    }

    return isPastDue(quiz.quizDueDate) ? "overdue" : "pending"
  }

  const getStatusCounts = () => {
    const total = allQuizzes.length
    let submitted = 0
    let pending = 0
    let overdue = 0

    allQuizzes.forEach((quiz) => {
      const status = getQuizStatus(quiz)
      switch (status) {
        case "submitted":
          submitted++
          break
        case "pending":
          pending++
          break
        case "overdue":
          overdue++
          break
      }
    })

    return { total, submitted, pending, overdue }
  }

  const getFilteredQuizzes = () => {
    let filtered = allQuizzes

    // Filter by status
    if (filterStatus !== "all") {
      filtered = filtered.filter((quiz) => getQuizStatus(quiz) === filterStatus)
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter((quiz) => {
        const title = (quiz.quizTitle || "").toLowerCase()
        const topic = (quiz.quiz_topic || "").toLowerCase()
        const subject = (quiz.subject || "").toLowerCase()
        const search = searchTerm.toLowerCase()
        return title.includes(search) || topic.includes(search) || subject.includes(search)
      })
    }

    return filtered
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "submitted":
        return <CheckCircle className="w-5 h-5 text-green-1000" />
      case "overdue":
        return <AlertCircle className="w-5 h-5 text-red-600" />
      default:
        return <Clock className="w-5 h-5 text-amber-600" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "submitted":
        return "border-t-green-500"
      case "overdue":
        return "border-t-red-500"
      default:
        return "border-t-amber-500"
    }
  }

  const getActionButton = (quiz, status) => {
    const submission = submissions.find((sub) => sub.quizId === quiz._id)

    switch (status) {
      case "submitted":
        return (
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleQuizClick(quiz)
            }}
            className="px-3 py-2 rounded-lg text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-all duration-300 shadow-md flex items-center justify-center gap-2 w-full text-sm"
          >
            <Eye size={16} />
            <span>View Results</span>
            <ChevronRight size={14} className="ml-auto" />
          </button>
        )
      case "overdue":
        return (
          <button
            disabled
            className="text-red-600 bg-red-50 px-3 py-2 rounded-lg cursor-not-allowed shadow-md flex items-center justify-center gap-2 w-full text-sm opacity-75"
          >
            <AlertCircle size={16} />
            <span>Overdue</span>
          </button>
        )
      default:
        return (
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleQuizClick(quiz)
            }}
            className="text-emerald-600 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 rounded-lg transition-all duration-300 shadow-md flex items-center justify-center gap-2 w-full text-sm"
          >
            <ArrowRight size={16} />
            <span>Take Quiz</span>
            <ChevronRight size={14} className="ml-auto" />
          </button>
        )
    }
  }

  const statusCounts = getStatusCounts()
  const filteredQuizzes = getFilteredQuizzes()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex items-center justify-center">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <Loader className="animate-spin text-indigo-600 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Loading quizzes</h2>
          <p className="text-sm sm:text-base text-gray-600">Please wait while we fetch your quizzes...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <AlertCircle className="text-red-500 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Something went wrong</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{error}</p>
          <button
            onClick={() => {
              setError(null)
              fetchQuizzes()
            }}
            className="px-4 sm:px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
      {/* Flash Message */}
      {flashMessage && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-xl max-w-md animate-fade-in flex items-center ${
            flashMessage.type === "success" ? "bg-green-500" : "bg-red-500"
          } text-white`}
        >
          {flashMessage.type === "success" ? (
            <CheckCircle className="mr-3 flex-shrink-0" size={20} />
          ) : (
            <AlertCircle className="mr-3 flex-shrink-0" size={20} />
          )}
          <p>{flashMessage.message}</p>
        </div>
      )}

      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        {/* Header Section with Stats, Search, and Filter */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-3 md:py-4">
            {/* Mobile Layout */}
            <div className="block lg:hidden">
              <div className="flex items-center gap-2">
                {/* Stats - Compact for mobile */}
                <div className="flex items-center gap-1 mr-1">
                  <button
                    onClick={() => setFilterStatus("all")}
                    className={`bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center transition-colors ${
                      filterStatus === "all" ? "bg-white/40" : "hover:bg-white/30"
                    }`}
                  >
                    <p className="text-white text-[10px] font-bold">{statusCounts.total}</p>
                    <p className="text-white/80 text-[8px]">Total</p>
                  </button>
                  <button
                    onClick={() => setFilterStatus("submitted")}
                    className={`bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center transition-colors ${
                      filterStatus === "submitted" ? "bg-white/40" : "hover:bg-white/30"
                    }`}
                  >
                    <p className="text-white text-[10px] font-bold">{statusCounts.submitted}</p>
                    <p className="text-white/80 text-[8px]">Submitted</p>
                  </button>
                  <button
                    onClick={() => setFilterStatus("pending")}
                    className={`bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center transition-colors ${
                      filterStatus === "pending" ? "bg-white/40" : "hover:bg-white/30"
                    }`}
                  >
                    <p className="text-white text-[10px] font-bold">{statusCounts.pending}</p>
                    <p className="text-white/80 text-[8px]">Pending</p>
                  </button>
                  <button
                    onClick={() => setFilterStatus("overdue")}
                    className={`bg-white/20 backdrop-blur-sm rounded-md px-1 py-0.5 text-center transition-colors ${
                      filterStatus === "overdue" ? "bg-white/40" : "hover:bg-white/30"
                    }`}
                  >
                    <p className="text-white text-[10px] font-bold">{statusCounts.overdue}</p>
                    <p className="text-white/80 text-[8px]">Overdue</p>
                  </button>
                </div>

                {/* Search */}
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-1.5 pl-7 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-xs"
                  />
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white/70" size={12} />
                </div>

                {/* Filter */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-medium border border-white/30"
                  >
                    <Filter size={12} />
                    <ChevronDown
                      size={10}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>

                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {[
                        { key: "all", label: "All" },
                        { key: "submitted", label: "Submitted" },
                        { key: "pending", label: "Pending" },
                        { key: "overdue", label: "Overdue" },
                      ].map((status) => (
                        <button
                          key={status.key}
                          onClick={() => {
                            setFilterStatus(status.key)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-2 py-1.5 text-xs text-left transition-colors hover:bg-gray-50 ${
                            filterStatus === status.key ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          {status.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:flex lg:items-center justify-between gap-4">
              {/* Stats - Left side */}
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  {
                    icon: <FileText className="w-4 h-4 opacity-80" />,
                    label: "Total",
                    value: statusCounts.total,
                    key: "all",
                  },
                  {
                    icon: <CheckCircle className="w-4 h-4 opacity-100" />,
                    label: "Submitted",
                    value: statusCounts.submitted,
                    key: "submitted",
                  },
                  {
                    icon: <Clock className="w-4 h-4 opacity-80" />,
                    label: "Pending",
                    value: statusCounts.pending,
                    key: "pending",
                  },
                  {
                    icon: <AlertCircle className="w-4 h-4 opacity-80" />,
                    label: "Overdue",
                    value: statusCounts.overdue,
                    key: "overdue",
                  },
                ].map((item, index) => (
                  <button
                    key={index}
                    onClick={() => setFilterStatus(item.key)}
                    className={`inline-flex items-center gap-2 border border-white/30 backdrop-blur-sm rounded-md px-3 py-1 text-white text-xs font-medium whitespace-nowrap transition-colors ${
                      filterStatus === item.key ? "bg-white/20" : "bg-white/10 hover:bg-white/15"
                    }`}
                  >
                    {item.icon}
                    <span>
                      {item.label}: <span className="font-bold">{item.value}</span>
                    </span>
                  </button>
                ))}
              </div>

              {/* Search and Filter - Right side */}
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search quizzes"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64 px-4 py-2 pl-10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-sm"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" size={14} />
                </div>

                {/* Filter */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-medium border border-white/30 hover:bg-white/30 transition-colors"
                  >
                    <Filter size={14} />
                    <span>
                      {filterStatus === "all" ? "All" : filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
                    </span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>

                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {[
                        { key: "all", label: "All" },
                        { key: "submitted", label: "Submitted" },
                        { key: "pending", label: "Pending" },
                        { key: "overdue", label: "Overdue" },
                      ].map((status) => (
                        <button
                          key={status.key}
                          onClick={() => {
                            setFilterStatus(status.key)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-3 py-2 text-sm text-left transition-colors hover:bg-gray-50 ${
                            filterStatus === status.key ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          {status.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-3 md:p-6">
            {/* Quizzes Grid */}
            {allQuizzes.length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                  <FileText className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No quizzes available</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  Your teacher hasn't shared any quizzes yet. Check back later for new quizzes.
                </p>
              </div>
            ) : filteredQuizzes.length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 text-amber-600 mb-4">
                  <Search className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No matching quizzes found</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  Try adjusting your search terms or filters to find what you're looking for.
                </p>
                <button
                  onClick={() => {
                    setSearchTerm("")
                    setFilterStatus("all")
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {filteredQuizzes.map((quiz) => {
                  const status = getQuizStatus(quiz)
                  const isOverdue = status === "overdue"

                  return (
                    <div
                      key={quiz._id}
                      className={`bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 flex flex-col relative group max-w-xs mx-auto w-full border-t-4 ${getStatusColor(
                        status,
                      )} ${isOverdue ? "opacity-75 cursor-not-allowed" : "hover:shadow-xl cursor-pointer"}`}
                      onClick={() => !isOverdue && handleQuizClick(quiz)}
                    >
                      {/* Quiz Header with Title */}
                      <div
                        className={`bg-gradient-to-r h-16 flex items-center justify-between px-4 relative ${
                          isOverdue ? "from-gray-400 to-gray-500" : "from-indigo-600 to-blue-600"
                        }`}
                      >
                        <h3 className="text-white font-semibold line-clamp-1 text-sm">{quiz.quizTitle}</h3>

                        {/* Status Icon */}
                        <div className="">{getStatusIcon(status)}</div>
                      </div>

                      {/* Quiz Content */}
                      <div className="p-4 flex-grow flex flex-col">
                        <div className="mb-3">
                          <div className="flex items-center text-gray-600">
                            <User size={14} className="mr-1.5 text-gray-400" />
                            <p className="line-clamp-1 text-sm">{quiz.subject || "General"}</p>
                          </div>
                        </div>

                        {quiz.quiz_topic && (
                          <p className="text-gray-600 text-xs mb-3 line-clamp-2">Topic: {quiz.quiz_topic}</p>
                        )}

                        {quiz.quizDueDate && (
                          <div
                            className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center self-start mb-3 ${
                              isOverdue ? "bg-red-50 text-red-700" : "bg-indigo-50 text-indigo-700"
                            }`}
                          >
                            <Calendar size={12} className="mr-1" />
                            Due: {formatDate(quiz.quizDueDate)}
                          </div>
                        )}

                        <div className="mt-auto space-y-1.5 pt-2 border-t border-gray-100">
                          {quiz.totalQuestions && (
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span className="flex items-center">
                                <FileText size={12} className="mr-1.5" />
                                Questions:
                              </span>
                              <span>{quiz.totalQuestions}</span>
                            </div>
                          )}
                        </div>

                        <div className="mt-3">{getActionButton(quiz, status)}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuizInfo
